import { useEffect, useRef, useState } from "react";
import { fetchUsageList } from "../../utils/apis";
import { formatPeriod, presetRange, rangeProblem } from "../../helpers/usageRange";
import UsageRangeFilter from "./UsageRangeFilter";
import UserUsageDialog from "./UserUsageDialog";
import { customerName } from "./customerName";

const PER_PAGE = 10;
const SEARCH_DELAY_MS = 300;

const StatCard = ({ title, tone, value }) => (
  <div className={`stat-card ${tone ? `stat-card--${tone}` : ""}`}>
    <div className="stat-card__head">
      <span className="stat-card__dot" />
      <span className="stat-card__label">{title}</span>
    </div>
    <div className="stat-card__value">{value}</div>
  </div>
);

const Usage = () => {
  const [range, setRange] = useState({ preset: "today", ...presetRange("today") });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const searchApplied = useRef("");
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", data: null });
  const [selected, setSelected] = useState(null);
  const { startDate, endDate } = range;
  const problem = rangeProblem(range);

  // the search box waits for a pause in typing before asking the server
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchApplied.current) return;
      searchApplied.current = next;
      setSearch(next);
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (problem) return undefined;
    let ignore = false;
    // keep showing the last rows (dimmed) while the next ones load
    setState((prev) => ({ ...prev, status: "loading" }));
    fetchUsageList({ startDate, endDate, search, page, perPage: PER_PAGE }).then((data) => {
      if (ignore) return;
      if (!data) {
        setState({ status: "error", data: null });
      } else if (data.ready === false) {
        setState({ status: "building", data: null });
      } else if (data.rows.length === 0 && data.page > 1 && data.totalPage >= 1) {
        setPage(data.totalPage); // the list got shorter than this page
      } else {
        setState({ status: "ready", data });
      }
    });
    return () => {
      ignore = true;
    };
  }, [startDate, endDate, search, page, attempt, problem]);

  const changeRange = (next) => {
    setRange(next);
    setPage(1);
  };

  const { status, data } = state;
  const rows = data?.rows ?? [];
  const totalRequests = data?.totalRequests ?? 0;
  const shown = problem || status === "error" || !data ? null : data;
  const firstRow = shown ? (shown.page - 1) * shown.perPage + 1 : 0;
  const periodLabel = problem ? "—" : formatPeriod(range);

  return (
    <div className="usage-page">
      <div className="usage-heading">
        <h2 className="page-title mb-1">Analytics</h2>
        <p className="text-muted mb-0">
          API requests made by each customer. Days are counted in UTC.
        </p>
      </div>

      <div className="usage-toolbar">
        <UsageRangeFilter value={range} onChange={changeRange} />
        <div className="form-group has-search usage-search">
          <svg
            className="form-search-feedback"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            className="form-control primary-form-control"
            placeholder="Search name, company or email"
            aria-label="Search customers"
            maxLength={100}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <div className="stat-grid usage-stats">
        <StatCard
          title="Total requests"
          value={problem || !data ? "—" : totalRequests.toLocaleString()}
        />
        <StatCard
          title="Customers with requests"
          tone="flow"
          value={problem || !data ? "—" : data.totalUsers.toLocaleString()}
        />
        <StatCard title="Period (UTC)" tone="slow" value={<span className="usage-period">{periodLabel}</span>} />
      </div>

      {problem ? (
        <p className="usage-state">Choose a valid period to see usage.</p>
      ) : status === "building" ? (
        <p className="usage-state" role="status">
          Analytics is still being set up, so there are no numbers yet. This is a one-time build and
          can take a while.{" "}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setAttempt((n) => n + 1)}>
            Check again
          </button>
        </p>
      ) : status === "error" ? (
        <p className="usage-state usage-state-error" role="alert">
          Could not load usage.{" "}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : !data ? (
        <p className="usage-state">Loading…</p>
      ) : (
        <div className={`table-responsive usage-table ${status === "loading" ? "usage-loading" : ""}`} aria-busy={status === "loading"}>
          <table className="table">
            <thead>
              <tr>
                <th className="user-header-item usage-col-customer">Customer</th>
                <th className="user-header-item usage-col-company">Company</th>
                <th className="user-header-item usage-num">Requests</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                return (
                  <tr key={row._id} className="data-row-verticle-middle">
                    <td className="user-data-item">
                      <button
                        type="button"
                        className="usage-customer"
                        onClick={() => setSelected(row)}
                        title="See requests per service"
                      >
                        <span className="usage-customer-name">{customerName(row)}</span>
                        {row.deleted && <span className="usage-badge">Deleted account</span>}
                        {row.email && customerName(row) !== row.email && (
                          <span className="usage-customer-email">{row.email}</span>
                        )}
                      </button>
                    </td>
                    <td className="user-data-item">{row.companyName || <span className="text-muted">—</span>}</td>
                    <td className="user-data-item usage-num">{row.requests.toLocaleString()}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3} className="usage-empty">
                    {search
                      ? `No customer matching “${search}” made requests in this period.`
                      : "No requests in this period."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {shown && shown.totalUsers > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
              <div>
                Showing {firstRow.toLocaleString()} to {(firstRow + rows.length - 1).toLocaleString()} of{" "}
                {shown.totalUsers.toLocaleString()} customers
              </div>
              <div className="d-flex gap-2 align-items-center">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`btn ${page <= 1 ? "btn-outline-secondary" : "btn-primary"}`}
                >
                  Previous
                </button>
                <span className="mx-3">
                  Page {shown.page} of {shown.totalPage}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= shown.totalPage}
                  className={`btn ${page >= shown.totalPage ? "btn-outline-secondary" : "btn-primary"}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selected && (
        <UserUsageDialog user={selected} initialRange={range} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};

export default Usage;
